import { useQuery } from '@tanstack/react-query'
import { api } from './client'

export interface WaveListItem { id: number; profileId: number; lagerCode: string; status: string }
export interface WaveList { items: WaveListItem[]; total: number; limit: number; offset: number }
export interface WaveReadiness {
  checkedAt: string; waveId: number; profileId: number; scopeHash: string; dossierHash: string | null
  checks: { key: string; category: 'preview' | 'live'; status: 'pass' | 'blocked' | 'unknown'; reasonCode: string; nextAction: string }[]
  previewReady: boolean; liveReady: false; members: { asin: string; sellerSku: string }[]
}
export interface RolloutPreviewGroup {
  clientRef: string; class?: 'ZUGPFERDE' | 'WACHSTUMSPOOL' | 'SAMMELBECKEN'
  members?: { asin?: string; sku?: string }[]
  campaignName?: string; adGroupName?: string; dailyBudgetEur?: number
  queries?: { sourceCampaignId: number; term: string }[]
}
export interface RolloutPreviewInput { scopeHash: string; dossierHash: string; groups: RolloutPreviewGroup[] }
export interface RolloutPreviewResult {
  version: 1; scopeHash: string; dossierHash: string | null; planHash: string; operationKey: string
  previewOnly: true; executable: false
  blockers: { reasonCode: string; nextAction: string; clientRef?: string; field?: string }[]
  drafts: {
    clientRef: string; class: NonNullable<RolloutPreviewGroup['class']>; members: { asin: string; sku: string }[]; operationKey: string
    campaign: { clientRef: string; adType: 'SP'; name: string; state: 'PAUSED'; targetingType: 'manual'; budgetType: 'daily'; dailyBudgetEur: number }
    adGroup: { clientRef: string; campaignClientRef: string; name: string; state: 'PAUSED' }
    keywords: {
      clientRef: string; adGroupClientRef: string; term: string; matchType: 'EXACT'; state: 'PAUSED'; tier: 'A' | 'B'
      source: { sourceCampaignId: number; term: string }; bidEur: number
      economics: { maxBidEur: number; cpcRuleBidEur: number | null; legacyAovCapEur: number | null; marginCapEur: number | null }
    }[]
  }[]
}

export function useWaves(profileId: number | null, offset = 0) {
  return useQuery({ queryKey: ['waves', 'list', profileId, offset], enabled: profileId !== null, retry: false,
    queryFn: ({ signal }) => api.get<WaveList>(`/waves/?profileId=${profileId}&limit=25&offset=${offset}`, { signal }) })
}
export function useWaveReadiness(waveId: number | null) {
  return useQuery({ queryKey: ['waves', 'readiness', waveId], enabled: waveId !== null, retry: false,
    queryFn: ({ signal }) => api.get<WaveReadiness>(`/waves/${waveId}/readiness/`, { signal }) })
}
// POST berechnet ausschliesslich eine Vorschau. Es gibt keinen Apply-Aufruf.
export function requestRolloutPreview(waveId: number, input: RolloutPreviewInput, signal?: AbortSignal) {
  return api.postLesend<RolloutPreviewResult>(`/waves/${waveId}/rollout-preview/`, input, { signal })
}

// ============================================================
//  Wellen-Economics (Shadow-v1) — Lesen und reine Vorschau.
//
//  Es gibt hier bewusst keine Mutation: die Oberflaeche zeigt den Stand, sie
//  baut ihn nicht und schaltet nichts scharf. Der Dossier-Aufbau und das Arming
//  sind gegatete Serverschritte (`import:write` bzw. `wave:arm`), und Shadow-v1
//  ist serverseitig ohnehin nicht scharfschaltbar.
// ============================================================

export interface WaveKpis {
  impressions: number
  clicks: number
  spend: number
  sales: number
  orders: number
  acos: number | null
  cpcHist: number | null
  cvr: number | null
}

export type WaveDeviationReason = 'reference_missing' | 'reference_zero' | null
/** Shadow-v1-Gebotsvertrag: ab V2 ist Sellerboard-AOV nur optionaler Vergleich. */
export const WAVE_ECONOMICS_CURRENT_VERSION = 2

export interface WaveQueryEconomics {
  cvr: number | null
  cpc_rule_bid: number | null
  /** Optionaler Sellerboard-Vergleich; begrenzt target_bid nicht. */
  legacy_aov_cap: number | null
  stahlia_margin_cap: number | null
  max_bid: number
  /** Wirksames Ziel aus bestaetigter Marge und CPC-Regel, auf Cent abgerundet. */
  target_bid: number
  blocked: boolean
  block_reasons: string[]
  deviation_pct: number | null
  deviation_reason: WaveDeviationReason
}

export interface WaveDossierQuery {
  term: string
  tier: string
  kpis: WaveKpis
  startBid: number | null
  sourceCampaignId: number
  legacyStartBid?: number | null
  economics?: WaveQueryEconomics
}

export interface WaveDossierEconomics {
  economics_mode: string
  eligible_for_arming: boolean
  version: number
  scope_hash: string
  dossier_hash: string
  built_at: string
  confirmed_margin_net: number | null
  usable: boolean
  block_reasons: string[]
  checked_at: string | null
  expires_at: string | null
  data_age_seconds: number | null
  sources: {
    provider: {
      enabled: boolean
      contract_version: string | null
      account: string | null
      seller_id: string | null
      marketplace_id: string | null
      validity_basis: string | null
      queried_at: string | null
      requested_skus: number
      returned_rows: number
      confirmed_rows: number
      error_code: string | null
      disabled_reasons: string[]
    }
    legacy: { aov: number; mature_cutoff: string; source: string }
  }
}

export interface WaveDossier {
  generatedAt: string
  targetAcos: number
  queries: WaveDossierQuery[]
  coverage: { mappedChildren: number; campaignsInWave: number }
  economics?: WaveDossierEconomics
}

export interface WaveProposal {
  id: number
  kind: string
  term: string
  tier: string
  sourceCampaignId: number
  startBid: string | null
  status: string
  dedupeKey: string
  effectiveBid: number
  applicable: boolean
  fenceReasons: string[]
}

export function useWaveDossier(waveId: number | null) {
  return useQuery({
    queryKey: ['waves', 'dossier', waveId],
    enabled: waveId !== null,
    retry: false,
    queryFn: ({ signal }) => api.get<{ dossier: WaveDossier }>(`/waves/${waveId}/dossier/`, { signal }),
  })
}

export function useWaveProposals(waveId: number | null) {
  return useQuery({
    queryKey: ['waves', 'proposals', waveId],
    enabled: waveId !== null,
    retry: false,
    queryFn: ({ signal }) => api.get<{ proposals: WaveProposal[] }>(`/waves/${waveId}/proposals/`, { signal }),
  })
}
